import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../../service/Api";
import { Loader2, Printer, Share2, Check, X, Copy, MessageSquare, Edit3, Shield, Info, FileCheck } from "lucide-react";
import CollateralPreview from "../../components/collateral/CollateralPreview";
import CollateralChatbot from "../../components/collateral/CollateralChatbot";
import VisualEditor from "../../components/visualEditor/VisualEditor";
import InspectorPanel from "../../components/visualEditor/InspectorPanel";
import SaveChangesDialog from "../../components/visualEditor/SaveChangesDialog";
import { loadFontsFromData } from "../../utils/fontLoader";
import { useImageProxy } from "../../hooks/useImageProxy";
import { useVisualEditor } from "../../hooks/useVisualEditor";
import { useAuth } from "../../context/AuthContext";
import { useReactToPrint } from "react-to-print";

export default function TailspinCollateral() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useAuth();

  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  // Version management
  const [allVersions, setAllVersions] = useState([]);
  const [currentVersionIndex, setCurrentVersionIndex] = useState(0);

  // Chatbot states
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Shareable link state
  const [linkCopied, setLinkCopied] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  
  // Compliance modal state
  const [showComplianceModal, setShowComplianceModal] = useState(false);

  // Sidebar state
  const [sidebarMode, setSidebarMode] = useState('chat'); // 'chat' | 'edit'
  const [toast, setToast] = useState(null);

  // Disabled image conversion - use original URLs
  const convertedBlobUrls = null;
  const imagesLoading = false;

  // Converted data with blob URLs
  const [convertedData, setConvertedData] = useState(null);

  const [dataVersion, setDataVersion] = useState(0);

  // Save functionality states
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const originalDataRef = useRef(null);

  /**
   * Handle visual editor data changes
   */
  const handleVisualDataChange = useCallback(async (newData) => {
    console.log("🎨 Visual edit - data changed:", newData);

    // Store original data on first edit
    if (originalDataRef.current === null) {
      originalDataRef.current = JSON.parse(JSON.stringify(data));
    }

    // Update local data immediately for real-time preview
    setData(newData);

    // Mark as unsaved
    setHasUnsavedChanges(true);

    // Increment version to force re-render
    setDataVersion(prev => prev + 1);
  }, [data]);

  // Visual editor hook
  const {
    selectedElement,
    isVisualEditMode,
    hoveredElement,
    handleElementClick,
    handleElementHover,
    handleElementLeave,
    updateProperty,
    toggleVisualEditMode,
    clearSelection,
    setIsVisualEditMode,
    setSelectedElement
  } = useVisualEditor(convertedData || data, handleVisualDataChange);

  // Wrap updateProperty to show toast notifications
  const updatePropertyWithFeedback = useCallback((path, value) => {
    console.log('🔧 Update requested:', path, '→', value);

    try {
      updateProperty(path, value);
      setToast({ type: 'success', message: 'Updated!' });
      setTimeout(() => setToast(null), 2000);
    } catch (error) {
      console.error('❌ Update failed:', error);
      setToast({ type: 'error', message: 'Update failed' });
      setTimeout(() => setToast(null), 3000);
    }
  }, [updateProperty]);

  const handleTabChange = (mode) => {
    if (mode === 'chat' && sidebarMode === 'edit' && hasUnsavedChanges) {
      setShowSaveDialog(true);
      return;
    }

    setSidebarMode(mode);
    if (mode === 'edit') {
      setIsVisualEditMode(true);
    } else {
      setIsVisualEditMode(false);
      clearSelection();
    }
  };

  const extractVersionContent = (version) => {
    // Check for triple-nested structure: version.collateral.collateral.collateral
    const tripleNested = version?.collateral?.collateral?.collateral;
    if (tripleNested && typeof tripleNested === "object") {
      return {
        data: tripleNested.data ?? null,
        template: tripleNested.template ?? null,
        compliance: tripleNested.compilance ?? tripleNested.compliance ?? null,
      };
    }

    // Check for double-nested structure: version.collateral.collateral
    const doubleNested = version?.collateral?.collateral;
    if (doubleNested && typeof doubleNested === "object") {
      return {
        data: doubleNested.data ?? null,
        template: doubleNested.template ?? null,
        compliance: doubleNested.compilance ?? doubleNested.compliance ?? null,
      };
    }

    // Check for single-nested structure: version.collateral
    const singleNested = version?.collateral;
    if (singleNested && typeof singleNested === "object") {
      return {
        data: singleNested.data ?? null,
        template: singleNested.template ?? null,
        compliance: singleNested.compilance ?? singleNested.compliance ?? null,
      };
    }

    // Fallback to direct properties
    return {
      data: version?.data ?? null,
      template: version?.template ?? null,
      compliance: version?.compilance ?? version?.compliance ?? null,
    };
  };

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);

    async function fetchData() {
      try {
        const response = await apiService.gettailspinCollateralBySlug(slug);

        console.log("Response:", response);

        // Handle array response (multiple versions)
        const versions = Array.isArray(response?.data?.data)
          ? response.data.data
          : [response?.data?.data].filter(Boolean);

        if (versions.length === 0) {
          setError("No collateral data found.");
          return;
        }

        setAllVersions(versions);
        setCurrentVersionIndex(0); // Always show the first (newest) version initially

        // Build chat history from versions
        const historyMessages = [];
        // Clone and reverse to process chronologically (oldest to newest)
        const chronologicalVersions = [...versions].reverse();

        chronologicalVersions.forEach((version, index) => {
          const description = version.description;

          if (description) {
            historyMessages.push({
              id: `hist-user-${version.id || index}`,
              type: 'user',
              content: description,
              timestamp: new Date(version.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            });

            historyMessages.push({
              id: `hist-bot-${version.id || index}`,
              type: 'bot',
              content: `✓ Version ${index + 1} generated successfully.`,
              timestamp: new Date(version.created_at || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            });
          }
        });

        setMessages(historyMessages);

        // Set initial data from index 0
        const initialVersion = versions[0];
        console.log("initialVersion", initialVersion);
        const { data: initialData, template: initialTemplate, compliance: initialCompliance } = extractVersionContent(initialVersion);
        setData(initialData);
        setTemplate(initialTemplate);
        setCompliance(initialCompliance);

        // Load fonts from data
        await loadFontsFromData(initialData);

        console.log("Full versions:", versions);
        console.log("Showing version:", 0);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch template or data.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [slug]);

  const handleVersionSwitch = async (index) => {
    if (index >= 0 && index < allVersions.length) {
      setCurrentVersionIndex(index);
      const version = allVersions[index];
      const { data: versionData, template: versionTemplate, compliance: versionCompliance } = extractVersionContent(version);
      setData(versionData);
      setTemplate(versionTemplate);
      setCompliance(versionCompliance);

      // Load fonts for the switched version
      await loadFontsFromData(versionData);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isSending) return;

    const messageContent = inputValue.trim();

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: messageContent,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsSending(true);

    try {
      // Send current version index to backend
      const response = await apiService.editTailspinCollateral(slug, messageContent, currentVersionIndex);

      if (response?.data?.success) {
        // Refresh all versions after edit
        const refreshResponse = await apiService.gettailspinCollateralBySlug(slug);
        const versions = Array.isArray(refreshResponse?.data?.data)
          ? refreshResponse.data.data
          : [refreshResponse?.data?.data].filter(Boolean);

        setAllVersions(versions);
        setCurrentVersionIndex(0); // New version is always at index 0

        // Update preview with new version
        const newVersion = versions[0];
        const { data: newData, template: newTemplate } = extractVersionContent(newVersion);
        setData(newData);
        setTemplate(newTemplate);

        // Load fonts for the new version
        await loadFontsFromData(newData);

        const botMessage = {
          id: Date.now() + 1,
          type: "bot",
          content: "✓ Collateral updated successfully! The preview has been refreshed with your changes.",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          data: newData,
          versions: versions
        };

        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error("Failed to update collateral");
      }
    } catch (err) {
      console.error("Error editing collateral:", err);

      const errorMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: (err.response?.data?.error) ? err.response?.data?.error : "❌ Sorry, I couldn't process your request. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  const handleInputChange = (e) => setInputValue(e.target.value);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleErrorReport = (errorMessage) => {
    // Set the error message in the input field
    setInputValue(errorMessage);
    // Switch to chat mode
    handleTabChange('chat');
    // Focus the input field if possible (using a small timeout to ensure DOM is ready)
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder="Describe your changes..."]');
      if (textarea) {
        textarea.focus();
        // Scroll to the bottom of the textarea to show the content
        textarea.scrollTop = textarea.scrollHeight;
      }
    }, 100);
  };

  const handleGetShareableLink = () => {
    if (!slug) return;
    const domain = user?.email?.split('@')[1].split('.')[0];
    // Use environment variable if available, otherwise fallback to current origin
   
    
    const baseUrl = process.env.REACT_APP_SHAREABLE_URL || "https://microsite.heyparrot.ai";
    const link = `${baseUrl}/${domain}/${slug}`;
    
    console.log("shareabelink", link);
    setShareableLink(link);
    setShowLinkModal(true);
  };

  const handleCopyLink = async () => {
    if (!shareableLink) return;

    try {
      await navigator.clipboard.writeText(shareableLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = shareableLink;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy link:', fallbackErr);
        alert(`Shareable link: ${shareableLink}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleQuickEdit = async (message) => {
    // Switch to chat mode if needed
    if (sidebarMode !== 'chat') {
      handleTabChange('chat');
    }

    if (!message.trim() || isSending) return;

    const messageContent = message.trim();

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: messageContent,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setIsSending(true);

    try {
      // Send current version index to backend
      const response = await apiService.editTailspinCollateral(slug, messageContent, currentVersionIndex);

      if (response?.data?.success) {
        // Refresh all versions after edit
        const refreshResponse = await apiService.gettailspinCollateralBySlug(slug);
        const versions = Array.isArray(refreshResponse?.data?.data)
          ? refreshResponse.data.data
          : [refreshResponse?.data?.data].filter(Boolean);

        setAllVersions(versions);
        setCurrentVersionIndex(0); // New version is always at index 0

        // Update preview with new version
        const newVersion = versions[0];
        const { data: newData, template: newTemplate, compliance: newCompliance } = extractVersionContent(newVersion);
        setData(newData);
        setTemplate(newTemplate);
        setCompliance(newCompliance);

        // Load fonts for the new version
        await loadFontsFromData(newData);

        const botMessage = {
          id: Date.now() + 1,
          type: "bot",
          content: "✓ Collateral updated successfully! The preview has been refreshed with your changes.",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          data: newData,
          versions: versions
        };

        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error("Failed to update collateral");
      }
    } catch (err) {
      console.error("Error editing collateral:", err);

      const errorMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: "❌ Sorry, I couldn't process your request. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Save changes to backend
   */
  const handleSaveChanges = async () => {
    setIsSaving(true);

    try {
      const currentCollateral = allVersions[currentVersionIndex];
      if (!currentCollateral?.id) {
        throw new Error('No collateral ID found');
      }

      console.log('💾 Saving collateral changes...', {
        id: currentCollateral.id,
        hasData: !!data,
        hasTemplate: !!template,
        title: currentCollateral.collateral?.collateral?.title
      });

      const response = await apiService.updateTailspinCollateral(
        currentCollateral.id,
        {
          collateral: {
            collateral: {
              title: currentCollateral.collateral.collateral.title,
              data: data,
              template: template  // Always include template
            }
          }
        }
      );

      if (response?.data?.success) {
        console.log('✅ Collateral saved successfully', {
          savedData: !!response?.data?.data,
          hasTemplate: !!template,
          hasData: !!data
        });

        // Update original data ref
        originalDataRef.current = JSON.parse(JSON.stringify(data));

        // Clear unsaved changes flag
        setHasUnsavedChanges(false);
        setShowSaveDialog(false);
        // Keep in edit mode
        setToast({ type: 'success', message: 'Saved successfully!' });
        setTimeout(() => setToast(null), 2000);
      } else {
        throw new Error(response?.data?.message || 'Failed to save');
      }
    } catch (error) {
      console.error('❌ Error saving collateral:', error);
      setToast({ type: 'error', message: `Failed to save: ${error.message}` });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Discard changes and revert to original data
   */
  const handleDiscardChanges = () => {
    if (originalDataRef.current) {
      setData(originalDataRef.current);
      setDataVersion(prev => prev + 1);
    }
    setHasUnsavedChanges(false);
    setShowSaveDialog(false);
    // Switch to chat mode
    handleTabChange('chat');
    originalDataRef.current = null;
  };

  /**
   * Cancel closing (stay in edit mode)
   */
  const handleCancelClose = () => {
    setShowSaveDialog(false);
  };

  // Image conversion disabled - just use original data
  useEffect(() => {
    if (!data) {
      setConvertedData(null);
      return;
    }

    // Use original data without conversion
    setConvertedData(data);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#FF6B5A' }} />
          <p style={{ color: '#0A0A10' }}>Loading collateral...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center p-6 rounded-lg" style={{ color: '#FF6B5A', backgroundColor: 'rgba(255, 107, 90, 0.1)' }}>
          <p className="font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  if (!template || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div style={{ color: '#0A0A10' }}>No template or data received.</div>
      </div>
    );
  }


  return (
    <div className="flex h-screen" style={{ backgroundColor: '#F8F7F4' }}>
      {/* Left Sidebar - Chatbot or Inspector - FIXED WIDTH */}
      <div className="w-96 flex flex-col flex-shrink-0 border-r border-gray-200 bg-white">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => handleTabChange('chat')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${sidebarMode === 'chat' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
          >
            <MessageSquare className="w-4 h-4" />
            Chat
          </button>
          <button
            onClick={() => handleTabChange('edit')}
            className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${sidebarMode === 'edit' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
          >
            <Edit3 className="w-4 h-4" />
            Editor
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden relative">
          <div className={`absolute inset-0 flex flex-col transition-opacity duration-200 ${sidebarMode === 'chat' ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}>
            <CollateralChatbot
              messages={messages}
              inputValue={inputValue}
              isSending={isSending}
              onInputChange={handleInputChange}
              onSendMessage={handleSendMessage}
              onKeyPress={handleKeyPress}
              allVersions={allVersions}
              currentVersionIndex={currentVersionIndex}
              onVersionSwitch={handleVersionSwitch}
            />
          </div>

          {sidebarMode === 'edit' && (
            <div className="absolute inset-0 z-10 h-full overflow-auto bg-white">
              <InspectorPanel
                selectedElement={selectedElement}
                onUpdateProperty={updatePropertyWithFeedback}
                onClose={clearSelection}
              />
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Collateral Preview with Visual Editor - TAKES REMAINING SPACE */}
      <div className="flex-1 overflow-y-auto border-l" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
        <VisualEditor
          isVisualEditMode={isVisualEditMode}
          selectedElement={selectedElement}
          hoveredElement={hoveredElement}
          handleElementClick={handleElementClick}
          handleElementHover={handleElementHover}
          handleElementLeave={handleElementLeave}
          clearSelection={clearSelection}
          toast={toast}
        >
          {/* Header Toolbar */}
          <div className="sticky top-0 z-10 border-b px-6 py-4 flex items-center justify-between no-print" style={{ backgroundColor: '#F8F7F4', borderColor: '#D7B472' }}>
            <h2 className="text-lg font-semibold" style={{ color: '#0A0A10' }}>Preview</h2>
            <div className="flex items-center gap-3">
              {hasUnsavedChanges && (
                <button
                  onClick={() => setShowSaveDialog(true)}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm bg-green-600 hover:bg-green-700"
                >
                  Save Changes
                </button>
              )}
              <button
                onClick={handleGetShareableLink}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                style={{ backgroundColor: '#FF6B5A' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e55a4a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FF6B5A';
                }}
                title="Get shareable link"
              >
                <Share2 className="w-4 h-4" />
                <span>Get Shareable Link</span>
              </button>
              {compliance && (
                <button
                  onClick={() => setShowComplianceModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                  style={{ backgroundColor: '#FF6B5A' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e55a4a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#FF6B5A';
                  }}
                  title="View compliance information"
                >
                  <FileCheck className="w-4 h-4" />
                  <span>Get Compliance</span>
                </button>
              )}
              <button
                onClick={reactToPrintFn}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors shadow-sm hover:shadow-md font-medium text-sm"
                style={{ backgroundColor: '#FF6B5A' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e55a4a'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B5A'; }}
                title="Print collateral"
              >
                <Printer className="w-4 h-4" />
                <span>Download as PDF</span>
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div ref={contentRef} className="p-6">
            <CollateralPreview
              key={`preview-${currentVersionIndex}-${dataVersion}`}
              template={template}
              data={convertedData || data}
              withContainer={false}
              onErrorReport={handleErrorReport}
              onQuickEdit={handleQuickEdit}
            />
          </div>
        </VisualEditor>
      </div>

      {/* Shareable Link Modal */}
      {showLinkModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowLinkModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#F8F7F4' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                Shareable Link
              </h3>
              <button
                onClick={() => setShowLinkModal(false)}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                style={{ color: '#0A0A10' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: '#0A0A10' }}>
                Copy this link to share:
              </label>
              <div className="flex items-center gap-2 p-3 rounded-lg border" style={{ borderColor: '#D7B472', backgroundColor: 'white' }}>
                <input
                  type="text"
                  value={shareableLink}
                  readOnly
                  className="flex-1 outline-none text-sm"
                  style={{ color: '#0A0A10' }}
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopyLink}
                  className="p-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: linkCopied ? '#10b981' : '#2464f7',
                    color: 'white'
                  }}
                  title="Copy link"
                >
                  {linkCopied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              {linkCopied && (
                <p className="text-sm mt-2" style={{ color: '#10b981' }}>
                  ✓ Link copied to clipboard!
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowLinkModal(false)}
                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                style={{
                  backgroundColor: '#2464f7',
                  color: 'white'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e4ed8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2464f7'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Modal */}
      {showComplianceModal && compliance && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowComplianceModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#F8F7F4' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>
                  Content Compliance
                </h3>
              </div>
              <button
                onClick={() => setShowComplianceModal(false)}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
                style={{ color: '#0A0A10' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-100">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-4xl font-bold text-blue-600">{compliance.score || 'N/A'}</span>
                    <span className="text-lg text-gray-600">/ 100</span>
                  </div>
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                      style={{ width: `${compliance.score || 0}%` }}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 italic">AI-Generated Verification Score</p>
              </div>
              
              {compliance.note && (
                <div className="pt-4 border-t border-blue-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Compliance Assessment:</p>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{compliance.note}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowComplianceModal(false)}
                className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                style={{
                  backgroundColor: '#2464f7',
                  color: 'white'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1e4ed8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2464f7'; }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Changes Dialog */}
      <SaveChangesDialog
        isOpen={showSaveDialog}
        onSave={handleSaveChanges}
        onDiscard={handleDiscardChanges}
        onCancel={handleCancelClose}
        isSaving={isSaving}
      />
    </div>
  );
}
